import { Link } from 'react-router-dom';
import { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  size?: 'normal' | 'large';
}

export default function ComicCard({ comic, size = 'normal' }: ComicCardProps) {
  const formatViews = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return String(n);
  };

  return (
    <Link
      to={`/comic/${comic.slug}`}
      className="group block cursor-pointer"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <div
        className={`relative overflow-hidden rounded-lg bg-background-100 ${
          size === 'large' ? 'aspect-[3/4]' : 'aspect-[2/3]'
        }`}
      >
        <img
          src={comic.cover}
          alt={`${comic.title} - 韩国漫画`}
          title={`${comic.title} - 韩国漫画免费在线阅读`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Status badge */}
        <div className="absolute top-2 left-2">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              comic.status === '连载中'
                ? 'bg-primary-500 text-background-50'
                : 'bg-secondary-500 text-background-50'
            }`}
          >
            {comic.status}
          </span>
        </div>

        {/* Rating */}
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-foreground-950/60 backdrop-blur-sm rounded-full px-2 py-0.5">
          <i className="ri-star-fill text-accent-500 text-xs"></i>
          <span className="text-background-50 text-xs">{comic.rating}</span>
        </div>

        {/* Title overlay on hover */}
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <p className="text-background-50 text-xs leading-relaxed line-clamp-2">{comic.description}</p>
        </div>
      </div>

      <div className="mt-3">
        <h3 className="font-heading text-foreground-900 text-sm md:text-base line-clamp-1 group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
          <span>{comic.author}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(comic.views)}
          </span>
        </div>
        {size === 'large' && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {comic.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}